import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import axios from 'axios';
import { getAllUnits } from '@api/departments-and-units/units.api.ts';
import {
  SERVICES_INITIAL_STATE,
  ServicesActionParams,
  ServicesState
} from '@modules/services/store/services.model.ts';
import {
  createService,
  deleteService,
  editService,
  togglePublish
} from '@api/services-api.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useServicesStore = create<ServicesState>()(
  devtools(
    (set, getState) => ({
      ...SERVICES_INITIAL_STATE,
      ...withCallState(getState),

      getDepartmentUnit: async () => {
        try {
          const unitsData = await getAllUnits();

          const data = unitsData.map((unit) => ({
            id: unit.id,
            title: unit.name,
            value: unit.name,
            type: unit.type
          }));

          set({
            departmentUnit: data
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createService: async ({
        data,
        state,
        handleClose
      }: ServicesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const response = await createService(data as any);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          if (state) state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_success_services_text', SnackbarType.Success);
          return response;
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
          return null;
        }
      },

      editService: async ({
        data,
        id,
        state,
        handleClose
      }: ServicesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editService(data as any, id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_services_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteService: async ({
        id,
        state,
        handleClose
      }: ServicesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await deleteService(id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_delete_service_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      togglePublish: async ({
        id,
        state,
        handleClose
      }: ServicesActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const data = await togglePublish(id as number);

          const snackbarText = data?.isPublished
            ? 'snackbar_published_service_text'
            : 'snackbar_unpublished_service_text';

          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(snackbarText, SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      }
    }),
    { name: 'ServicesStore' }
  )
);

export default useServicesStore;
